//this file responsible for creating, showing and repeating calculation task.

import { ATaskConfiguration, ITaskConfiguration } from './TaskDefinitions';
import { CalculationDifficultyOptions } from '../../../../../commands/practitioner/enums/CalculationDifficultyOptions';
import { TimelineTask, MovingTargetTaskConfiguration } from './MovingTargetTask';
import { SubEvent } from 'sub-events';
import CONSTANTS from '../../../CONSTANTS/CONSTANTS';
import TaskScene from '../scene/TaskScene';
import Phaser from 'phaser';

export class CalculationTaskConfiguration extends ATaskConfiguration {
  mode: number = CalculationDifficultyOptions.Off;
  timeBetweenCalculations: number = CONSTANTS.calculation.timeBetweenCalculations;
  timeOffsetCalculations: number = CONSTANTS.calculation.timeOffsetCalculations;

  constructor(seed: number) {
    super(seed);
  }

  public Copy(): CalculationTaskConfiguration {
    let config = new CalculationTaskConfiguration(this.seed);

    config.enabled = this.enabled;
    config.mode = this.mode;
    config.timeOffsetCalculations = this.timeOffsetCalculations;
    config.timeBetweenCalculations = this.timeBetweenCalculations;

    return config;
  }

  ApplyDifficulty(difficultyConfig: ITaskConfiguration): void {
    let difConfig = difficultyConfig as CalculationTaskConfiguration;
    this.timeBetweenCalculations = difConfig.timeBetweenCalculations;
    this.timeOffsetCalculations = difConfig.timeOffsetCalculations;
  }
}

export class CalculationInputEvent {
  // Whether the input was done at the correct point or not. True == correct
  public readonly input: boolean;
  public readonly Task: CalculationTask;

  constructor(input: boolean, task: CalculationTask) {
    this.input = input;
    this.Task = task;
  }
}

export class CalculationTask extends TimelineTask<CalculationTaskConfiguration> {
  public static readonly CALCULATION_TIMELINE = 'calculation';
  keyPressesDetected: number = 0;
  text?: Phaser.GameObjects.Text;
  readonly OnCalculationResult: SubEvent<CalculationInputEvent> = new SubEvent();
  timelineActive: boolean = false;
  public handleCalculationButtonPress(): void {
    if (this.timelineActive) this.keyPressesDetected++;
  }
  // eslint-disable-next-line @typescript-eslint/no-useless-constructor
  constructor(configuration: CalculationTaskConfiguration, scene: Phaser.Scene) {
    super(configuration, scene, 'CalculationTask');
  }

  Create(configuration: CalculationTaskConfiguration, scene: Phaser.Scene): void {
    this.text = this.createText(scene);
    this.applyCurrentThemeTextColor(scene);
    this.text.setVisible(false);
    this.OnUpdate(configuration, this.configuration, scene, true);
  }

  public RebuildAfterResize(scene: Phaser.Scene) {
    const { width, height } = this.GetCurrenScreenSize(scene);
    const center = this.GetCenterPosition(scene);
    const y = center.y / CONSTANTS.calculation.positionDividerY;

    if (this.text) {
      const size = Math.round(this.CalculateScalingFactor(width, height));
      this.text.setStyle({ fontSize: `${size}px`, fontFamily: CONSTANTS.fontStyle as any });
      this.text.setText(this.text.text);
      this.text.setPosition(center.x, y);
    }
  }

  OnUpdate(
    oldConfiguration: CalculationTaskConfiguration,
    configuration: CalculationTaskConfiguration,
    scene: Phaser.Scene,
    firstCreateCall: boolean
  ) {
    this.CleanupInteractionButton();
    this.applyCurrentThemeTextColor(scene);

    let calculation = this.createCalculation(configuration, scene);
    this.ReplaceTimeline(CalculationTask.CALCULATION_TIMELINE, calculation);
  }
  Stop(scene: Phaser.Scene): void {
    super.Stop(scene);
    this.CleanupInteractionButton();
  }

  createText(scene: Phaser.Scene) {
    let center = this.GetCenterPosition(scene);
    let y = center.y / CONSTANTS.calculation.positionDividerY;
    let { width, height } = this.GetCurrenScreenSize(scene);
    let fontSettings = `${this.CalculateScalingFactor(width, height)}px ${CONSTANTS.fontStyle}`;
    return scene.add
      .text(center.x, y, 'test', {
        font: fontSettings,
      })
      .setOrigin(CONSTANTS.calculation.setOrigin);
  }

  CleanupInteractionButton() {
    this.keyPressesDetected = 0;
    this.timelineActive = false;
  }

  GetCenterPosition(scene: Phaser.Scene): Phaser.Math.Vector2 {
    const width: number = Number(scene.game.canvas.width);
    const height: number = Number(scene.game.canvas.height);
    const x = width / 2;
    const y = height / 2;
    return new Phaser.Math.Vector2(x, y);
  }

  GetCurrenScreenSize(scene: Phaser.Scene) {
    const width: number = scene.game.canvas.width;
    let height: number = width * CONSTANTS.movingTarget.heightPercent; // Maintain aspect ratio (height = 60% of width)

    height = Math.min(height, scene.game.canvas.height); // Ensure height does not exceed the actual canvas height

    return { width, height };
  }

  OnSetVisible(active: boolean): void {
    if (!this.text) {
      return;
    }
    this.text.active = active;
    this.text.setVisible(active);
  }

  private createCalculation(configuration: CalculationTaskConfiguration, scene: Phaser.Scene): Phaser.Tweens.Timeline {
    // Assign timeline
    switch (configuration.mode) {
      case CalculationDifficultyOptions.Off:
        return this.CreateCalculation(configuration, scene, 0, 0, 0);
      case CalculationDifficultyOptions.Easy:
        return this.CreateCalculation(configuration, scene, 1, 0, 9);
      case CalculationDifficultyOptions.Medium:
        return this.CreateCalculation(configuration, scene, 1, 8, 15);
      case CalculationDifficultyOptions.Hard:
        return this.CreateCalculation(configuration, scene, 10, 0, 99);

      default: {
        console.error('No factory method known for calculation: ' + configuration.mode);
        return this.CreateCalculation(configuration, scene, 0, 0, 0);
      }
    }
  }

  private CreateCalculation(
    configuration: CalculationTaskConfiguration,
    scene: Phaser.Scene,
    minRange: integer,
    midRange: integer,
    maxRange: integer
  ): Phaser.Tweens.Timeline {
    let duration = configuration.timeBetweenCalculations;
    let keyboard = scene.input.keyboard;
    let up = scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.UP);
    let isKeyDownPreviousFrame = false;
    let selectedCalculation: undefined | boolean = undefined;

    if (minRange === 0 || maxRange === 0) {
      this.text?.setVisible(false);
      return scene.tweens.createTimeline();
    } else {
      let center = this.GetCenterPosition(scene);
      let timeline = scene.tweens.createTimeline();
      let ts = scene as TaskScene;

      this.text?.setVisible(false);
      this.text?.setX(center.x);

      const follower = { t: 0 };

      timeline.add({
        targets: follower,
        t: { from: 0, to: 1 },
        duration: duration,

        onUpdate: tween => {
          if (!this.timelineActive) {
            this.timelineActive = true;
          }
          let newSpeed = configuration.timeBetweenCalculations - configuration.timeOffsetCalculations;
          tween.setTimeScale(duration / newSpeed);
          // Keep position/scale in sync while visible
          const { width, height } = this.GetCurrenScreenSize(scene);
          const ctr = this.GetCenterPosition(scene);
          const yNow = ctr.y / CONSTANTS.calculation.positionDividerY;
          const size = Math.round(this.CalculateScalingFactor(width, height));
          if (this.text) {
            // Only set when needed to reduce layout churn
            if (Math.round(parseFloat(this.text.style.fontSize as any)) !== size) {
              this.text.setStyle({ fontSize: `${size}px`, fontFamily: CONSTANTS.fontStyle as any });
            }
            this.text.setPosition(ctr.x, yNow);
          }

          if (!ts.isTablet) {
            if (ts.calledBy === 'client') {
              let isKeyDown = keyboard.checkDown(up);

              if (isKeyDown && !isKeyDownPreviousFrame) {
                this.keyPressesDetected++;
              }

              isKeyDownPreviousFrame = isKeyDown;
            }
          }
        },
        onComplete: () => {
          this.text?.setVisible(false);
          if (ts.calledBy === 'client') {
            if (selectedCalculation !== undefined) {
              if (selectedCalculation) {
                this.OnCalculationResult.emit(new CalculationInputEvent(this.keyPressesDetected === 1, this));
              } else if (!selectedCalculation) {
                this.OnCalculationResult.emit(new CalculationInputEvent(this.keyPressesDetected === 0, this));
              }
            }
          }
        },
      });

      timeline.add({
        targets: follower,
        t: { from: 0, to: 1 },
        duration: duration,
        onStart: () => {},
        onUpdate: tween => {
          if (this.timelineActive) {
            this.timelineActive = false;
          }
          let newSpeed = configuration.timeBetweenCalculations - configuration.timeOffsetCalculations;
          tween.setTimeScale(duration / newSpeed);
        },
        onComplete: tween => {
          // select what to show. Right or not answer
          let isRightAnswer = this.random.nextBool();
          if (typeof isRightAnswer === 'number') return;
          selectedCalculation = isRightAnswer;

          const min = this.random.between(minRange, maxRange);

          const max = this.random.between(minRange, maxRange);
          this.applyCurrentThemeTextColor(scene);
          // Ensure size/position reflect current canvas at the moment we show text
          const { width, height } = this.GetCurrenScreenSize(scene);
          const ctr = this.GetCenterPosition(scene);
          const yNow = ctr.y / CONSTANTS.calculation.positionDividerY;
          const size = Math.round(this.CalculateScalingFactor(width, height));
          if (this.text) {
            this.text.setStyle({ fontSize: `${size}px`, fontFamily: CONSTANTS.fontStyle as any });
            this.text.setPosition(ctr.x, yNow);
          }
          this.text?.setVisible(true);

          if (midRange > 0) {
            const mid = this.random.between(minRange, midRange);
            this.text?.setText(`${mid} + ${min + max} = ${isRightAnswer ? min + mid + max : mid + max}`);
          } else {
            this.text?.setText(`${min} + ${max} = ${isRightAnswer ? min + max : max + max - min}`);
          }

          this.keyPressesDetected = 0;
        },
      });

      timeline.loop = -1;
      return timeline;
    }
  }

  CalculateScalingFactor(screenWidth: number, screenHeight: number) {
    const referenceWidth = CONSTANTS.baseScreenWidth;
    const referenceHeight = CONSTANTS.baseScreenHeight;
    const baseFont = CONSTANTS.fontSize;

    const widthScale = screenWidth / referenceWidth;
    const heightScale = screenHeight / referenceHeight;

    const averageScale = (widthScale + heightScale) / 1.3;

    return baseFont * averageScale;
  }

  private applyCurrentThemeTextColor(scene: Phaser.Scene) {
    try {
      const ts = scene as TaskScene;
      const config = ts.composite?.configuration;
      const movingCfg = config?.GetConfiguration(MovingTargetTaskConfiguration);
      const themeName = (movingCfg as any)?.theme?.name;
      const isChild = themeName === 'child';
      this.text?.setColor(
        isChild ? CONSTANTS.calculation.calculationChildColor : CONSTANTS.calculation.calculationDefaultColor
      );
    } catch {
      console.log('calculation color is not set properly');
      this.text?.setColor(CONSTANTS.calculation.calculationDefaultColor);
    }
  }
}
